// client/src/pages/GuestCreateDeal.js
// Tranche 2 — Formulaire PUBLIC de création de deal (onboarding marchand différé).
// Un visiteur sans compte crée un deal -> capture email -> écran de confirmation.
// FR / AR (RTL, police Cairo via body.rtl). react-icons, aucun emoji.
// Maxula

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiUploadCloud, FiX, FiCheckCircle, FiLogIn, FiArrowRight, FiTag, FiPackage, FiDownloadCloud } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { dealAPI, categoryAPI, cityAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { validateImageFiles } from '../utils/imageValidation';
import { useLanguage } from '../i18n/LanguageContext';

const STR = {
  fr: {
    title: 'Publiez votre deal',
    subtitle: "Décrivez votre offre, on s'occupe du reste. Vous activerez votre compte par email juste après.",
    sellWhat: 'Que voulez-vous vendre ?',
    typeService: 'Deal / Service', typeServiceDesc: 'Offre, bon, prestation',
    typePhysical: 'Produit physique', typePhysicalDesc: 'Article à livrer',
    typeDigital: 'Produit digital', typeDigitalDesc: 'Fichier, accès en ligne',
    laterPhysical: 'Livraison, stock et variantes se configurent ensuite dans votre tableau de bord.',
    laterDigital: 'Le fichier à livrer s’ajoute ensuite dans votre tableau de bord.',
    stock: 'Stock', salePrice: 'Prix de vente (MAD)',
    business: "Nom de l'enseigne", businessType: "Type", phone: 'Téléphone',
    typeMerchant: 'Commerçant', typePromoteur: 'Promoteur', typeAgence: 'Agence',
    email: 'Votre email', dealTitle: 'Titre du deal', desc: 'Description',
    category: 'Catégorie', city: 'Ville', selectCat: 'Choisir une catégorie', selectCity: 'Choisir une ville',
    origPrice: 'Prix original (MAD)', price: 'Prix remisé (MAD)', qty: 'Quantité', unlimited: 'Illimité',
    images: 'Photos du deal', addImages: 'Ajouter des photos', recommended: 'Recommandé : 880 × 340 px, < 300 Ko',
    submit: 'Publier mon deal', submitting: 'Envoi…',
    successTitle: 'Votre deal est enregistré !', successMsg: "Vérifiez votre email pour activer votre compte et publier votre deal. Après activation, complétez votre vérification (KYC) — votre deal passera alors en ligne.",
    loginTitle: 'Vous avez déjà un compte marchand', loginMsg: 'Connectez-vous pour publier ce deal depuis votre tableau de bord.',
    upgradeTitle: 'Un compte existe avec cet email', upgradeMsg: 'Connectez-vous, puis activez votre espace marchand pour publier.',
    goLogin: 'Se connecter', backHome: "Retour à l'accueil",
    reqBusiness: "Nom de l'enseigne requis", reqEmail: 'Email valide requis', reqTitle: 'Titre requis', reqDesc: 'Description requise'
  },
  ar: {
    title: 'انشر عرضك',
    subtitle: 'صف عرضك ونحن نتكفل بالباقي. ستفعّل حسابك عبر البريد مباشرة بعد ذلك.',
    sellWhat: 'ماذا تريد أن تبيع؟',
    typeService: 'عرض / خدمة', typeServiceDesc: 'عرض، قسيمة، خدمة',
    typePhysical: 'منتج مادي', typePhysicalDesc: 'منتج يتم توصيله',
    typeDigital: 'منتج رقمي', typeDigitalDesc: 'ملف، وصول عبر الإنترنت',
    laterPhysical: 'يتم ضبط التوصيل والمخزون والخيارات لاحقًا من لوحة التحكم.',
    laterDigital: 'تتم إضافة الملف المراد تسليمه لاحقًا من لوحة التحكم.',
    stock: 'المخزون', salePrice: 'سعر البيع (درهم)',
    business: 'اسم المتجر', businessType: 'النوع', phone: 'الهاتف',
    typeMerchant: 'تاجر', typePromoteur: 'مطوّر', typeAgence: 'وكالة',
    email: 'بريدك الإلكتروني', dealTitle: 'عنوان العرض', desc: 'الوصف',
    category: 'الفئة', city: 'المدينة', selectCat: 'اختر فئة', selectCity: 'اختر مدينة',
    origPrice: 'السعر الأصلي (درهم)', price: 'السعر بعد الخصم (درهم)', qty: 'الكمية', unlimited: 'غير محدود',
    images: 'صور العرض', addImages: 'إضافة صور', recommended: 'موصى به: 880 × 340 بكسل، أقل من 300 كيلوبايت',
    submit: 'انشر عرضي', submitting: 'جارٍ الإرسال…',
    successTitle: 'تم تسجيل عرضك!', successMsg: 'تحقق من بريدك لتفعيل حسابك ونشر عرضك. بعد التفعيل، أكمل التحقق (KYC) — وعندها سيُنشر عرضك.',
    loginTitle: 'لديك حساب تاجر بالفعل', loginMsg: 'سجّل الدخول لنشر هذا العرض من لوحة التحكم.',
    upgradeTitle: 'يوجد حساب بهذا البريد', upgradeMsg: 'سجّل الدخول ثم فعّل مساحة التاجر للنشر.',
    goLogin: 'تسجيل الدخول', backHome: 'العودة للرئيسية',
    reqBusiness: 'اسم المتجر مطلوب', reqEmail: 'بريد صالح مطلوب', reqTitle: 'العنوان مطلوب', reqDesc: 'الوصف مطلوب'
  }
};

const GuestCreateDeal = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { lang, isRTL } = useLanguage();
  const L = STR[lang === 'ar' ? 'ar' : 'fr'];

  // Un marchand/admin connecté n'a rien à faire ici -> espace dédié.
  useEffect(() => {
    if (user && (user.role === 'merchant' || user.role === 'admin')) {
      navigate('/merchant/deals/new', { replace: true });
    }
  }, [user, navigate]);

  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [images, setImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // 'created' | 'login' | 'upgrade'
  const [form, setForm] = useState({
    businessName: '', businessType: 'merchant', phone: '', email: '',
    title: '', description: '', category: '', city: '',
    productType: 'service',
    originalPrice: '', price: '', quantity: '', unlimited: false, website_hp: ''
  });

  useEffect(() => {
    (async () => {
      try { const r = await categoryAPI.getAll(); setCategories(r.data?.data || r.data || []); } catch (e) {}
      try { const r = await cityAPI.getAll({ active: true }); setCities(r.data?.data || r.data?.cities || r.data || []); } catch (e) {}
    })();
  }, []);

  const onField = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onPickImages = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = null;
    const room = Math.max(0, 8 - images.length);
    const slice = files.filter((f) => f.type && f.type.startsWith('image/')).slice(0, room);
    const { valid, rejected } = await validateImageFiles(slice);
    rejected.forEach((r) => toast.warn(`${r.name} ignoré : ${r.reason}`));
    if (!valid.length) return;
    setImages((prev) => [...prev, ...valid.map((file) => ({ file, url: URL.createObjectURL(file) }))]);
  };
  const removeImage = (i) => setImages((prev) => prev.filter((_, idx) => idx !== i));

  const validate = () => {
    if (!form.businessName.trim()) { toast.error(L.reqBusiness); return false; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) { toast.error(L.reqEmail); return false; }
    if (!form.title.trim()) { toast.error(L.reqTitle); return false; }
    if (!form.description.trim()) { toast.error(L.reqDesc); return false; }
    return true;
  };

  const submit = async () => {
    if (!validate()) return;
    try {
      setSubmitting(true);
      const fd = new FormData();
      fd.append('businessName', form.businessName.trim());
      fd.append('businessType', form.businessType);
      fd.append('email', form.email.trim().toLowerCase());
      if (form.phone) fd.append('phone', form.phone.trim());
      fd.append('title', form.title.trim());
      fd.append('description', form.description.trim());
      fd.append('productType', form.productType || 'service');
      if (form.category) fd.append('category', form.category);
      if (form.city) fd.append('city', form.city);
      if (form.originalPrice) fd.append('originalPrice', String(Number(form.originalPrice) || 0));
      if (form.price) fd.append('discountedPrice', String(Number(form.price) || 0));
      if (form.unlimited) fd.append('quantity', '0');
      else if (form.quantity) fd.append('quantity', String(parseInt(form.quantity, 10) || 0));
      fd.append('website_hp', form.website_hp); // honeypot (vide pour un humain)
      images.forEach(({ file }) => fd.append('images', file));

      const res = await dealAPI.createGuest(fd);
      setResult(res.data?.action || 'created');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      const msg = e?.response?.data?.message || "Une erreur est survenue. Réessayez.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-pink-400';
  const labelCls = 'block text-sm font-medium text-gray-700 mb-1';

  // ----- Écrans de résultat -----
  if (result) {
    const isCreated = result === 'created';
    const head = isCreated ? L.successTitle : result === 'login' ? L.loginTitle : L.upgradeTitle;
    const body = isCreated ? L.successMsg : result === 'login' ? L.loginMsg : L.upgradeMsg;
    return (
      <div className="max-w-xl mx-auto px-4 py-16 text-center" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center ${isCreated ? 'bg-green-100 text-green-600' : 'bg-pink-100 text-[#e11d7b]'}`}>
          {isCreated ? <FiCheckCircle size={32} /> : <FiLogIn size={30} />}
        </div>
        <h1 className="mt-5 text-2xl font-bold text-gray-900">{head}</h1>
        <p className="mt-3 text-gray-600 leading-relaxed">{body}</p>
        <div className="mt-7 flex items-center justify-center gap-3">
          {isCreated ? (
            <button onClick={() => navigate('/')} className="px-6 py-3 rounded-lg bg-[#e11d7b] text-white font-semibold hover:opacity-90">
              {L.backHome}
            </button>
          ) : (
            <button onClick={() => navigate('/login')} className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#e11d7b] text-white font-semibold hover:opacity-90">
              {L.goLogin} <FiArrowRight className={isRTL ? 'rotate-180' : ''} />
            </button>
          )}
        </div>
      </div>
    );
  }

  // ----- Formulaire -----
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="text-center mb-8">
        <h1 className="text-3xl font-extrabold text-gray-900">{L.title}</h1>
        <p className="mt-2 text-gray-500">{L.subtitle}</p>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 space-y-5">
        {/* Sélecteur de type d'annonce — marketplace multi-types (Option A : léger) */}
        <div>
          <label className={labelCls}>{L.sellWhat}</label>
          <div className="grid sm:grid-cols-3 gap-3">
            {[
              { key: 'service', Icon: FiTag, label: L.typeService, desc: L.typeServiceDesc },
              { key: 'physical', Icon: FiPackage, label: L.typePhysical, desc: L.typePhysicalDesc },
              { key: 'digital', Icon: FiDownloadCloud, label: L.typeDigital, desc: L.typeDigitalDesc }
            ].map(({ key, Icon, label, desc }) => {
              const active = form.productType === key;
              return (
                <button
                  type="button"
                  key={key}
                  onClick={() => setForm((f) => ({ ...f, productType: key }))}
                  className={`text-start border rounded-xl p-3 transition flex items-start gap-3 ${active ? 'border-[#e11d7b] bg-pink-50 ring-2 ring-pink-200' : 'border-gray-200 hover:border-pink-300 hover:bg-pink-50/40'}`}
                >
                  <Icon size={22} className={active ? 'text-[#e11d7b]' : 'text-gray-400'} />
                  <span>
                    <span className={`block text-sm font-semibold ${active ? 'text-[#e11d7b]' : 'text-gray-800'}`}>{label}</span>
                    <span className="block text-xs text-gray-500 mt-0.5">{desc}</span>
                  </span>
                </button>
              );
            })}
          </div>
          {form.productType === 'physical' && (
            <p className="mt-2 text-xs text-gray-500">{L.laterPhysical}</p>
          )}
          {form.productType === 'digital' && (
            <p className="mt-2 text-xs text-gray-500">{L.laterDigital}</p>
          )}
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>{L.business} <span className="text-red-500">*</span></label>
            <input name="businessName" value={form.businessName} onChange={onField} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>{L.businessType}</label>
            <select name="businessType" value={form.businessType} onChange={onField} className={inputCls}>
              <option value="merchant">{L.typeMerchant}</option>
              <option value="promoteur">{L.typePromoteur}</option>
              <option value="agence">{L.typeAgence}</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>{L.email} <span className="text-red-500">*</span></label>
            <input name="email" type="email" value={form.email} onChange={onField} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>{L.phone}</label>
            <input name="phone" value={form.phone} onChange={onField} className={inputCls} />
          </div>
        </div>

        <div>
          <label className={labelCls}>{L.dealTitle} <span className="text-red-500">*</span></label>
          <input name="title" value={form.title} onChange={onField} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>{L.desc} <span className="text-red-500">*</span></label>
          <textarea name="description" value={form.description} onChange={onField} rows={4} className={inputCls} />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>{L.category}</label>
            <select name="category" value={form.category} onChange={onField} className={inputCls}>
              <option value="">{L.selectCat}</option>
              {categories.map((c) => (
                <option key={c.id ?? c._id} value={c.slug ?? c.id ?? c._id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>{L.city}</label>
            <select name="city" value={form.city} onChange={onField} className={inputCls}>
              <option value="">{L.selectCity}</option>
              {cities.map((c) => (
                <option key={c.id ?? c._id} value={c.id ?? c._id}>{c.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          {form.productType === 'service' && (
            <div>
              <label className={labelCls}>{L.origPrice}</label>
              <input name="originalPrice" type="number" min="0" value={form.originalPrice} onChange={onField} className={inputCls} />
            </div>
          )}
          <div>
            <label className={labelCls}>{form.productType === 'service' ? L.price : L.salePrice}</label>
            <input name="price" type="number" min="0" value={form.price} onChange={onField} className={inputCls} />
          </div>
          {form.productType !== 'digital' && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-gray-700">{form.productType === 'physical' ? L.stock : L.qty}</label>
                <label className="flex items-center gap-1.5 cursor-pointer select-none text-sm font-medium text-gray-600">
                  <input type="checkbox" checked={form.unlimited} onChange={(e) => { const v = e.target.checked; setForm((f) => ({ ...f, unlimited: v })); }} className="w-4 h-4 rounded border-gray-300 text-pink-500 focus:ring-pink-400" />
                  {L.unlimited}
                </label>
              </div>
              <input name="quantity" type="number" min="0" value={form.unlimited ? '' : form.quantity} onChange={onField} disabled={form.unlimited} placeholder={form.unlimited ? L.unlimited : ''} className={`${inputCls}${form.unlimited ? ' bg-gray-50 text-gray-400' : ''}`} />
            </div>
          )}
        </div>

        <div>
          <label className={labelCls}>{L.images}</label>
          <p className="text-xs text-gray-400 mb-2">{L.recommended}</p>
          <div className="flex flex-wrap gap-3">
            {images.map((img, i) => (
              <div key={i} className="relative w-24 h-20 rounded-lg overflow-hidden border border-gray-200">
                <img src={img.url} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 bg-black/50 text-white rounded-full p-1 hover:bg-black/70">
                  <FiX size={12} />
                </button>
              </div>
            ))}
            {images.length < 8 && (
              <label className={`${images.length === 0 ? 'w-full h-32' : 'w-24 h-20'} rounded-lg border-2 border-dashed border-[#e11d7b] bg-pink-50 flex flex-col items-center justify-center text-[#e11d7b] cursor-pointer hover:border-[#e11d7b] hover:bg-pink-100 transition`}>
                <FiUploadCloud size={images.length === 0 ? 30 : 20} />
                <span className={`mt-1 ${images.length === 0 ? 'text-sm font-medium' : 'text-[11px]'}`}>{L.addImages}</span>
                <input type="file" accept="image/*" multiple onChange={onPickImages} className="hidden" />
              </label>
            )}
          </div>
        </div>

        {/* Honeypot anti-bot : invisible pour un humain */}
        <input
          type="text" name="website_hp" value={form.website_hp} onChange={onField}
          tabIndex={-1} autoComplete="off" aria-hidden="true"
          style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
        />

        <button
          onClick={submit}
          disabled={submitting}
          className="w-full py-3 rounded-xl bg-[#e11d7b] text-white font-bold hover:opacity-90 disabled:opacity-60 transition"
        >
          {submitting ? L.submitting : L.submit}
        </button>
      </div>
    </div>
  );
};

export default GuestCreateDeal;
